import React, {Component} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import _ from 'lodash';
import {isPromise} from '@elixir/platform';
import {log} from '@elixir/logger';
import Button from '../button';
import buttonStyles from '../button/Button-hashed.scss';
import styles from './Modal-hashed.scss';
import defaultTranslator from '@elixir/translator';
import getIconClasses from '../../iconUtils';
import { withModal } from './Modal';
import {withTheme, defaultTheme} from '@elixir/theme';

/**
 * The `ModalDialog` component is content that can be rendered by the `ModalService`.
 */
export class ModalDialog extends Component {

    static propTypes = {
    /**
         * The title of the modal content
     */
        title: PropTypes.string.isRequired,
        /**
         * The label for the action button, defaults to `Ok` if not provided
         */
        actionBtnLabel: PropTypes.string.isRequired,
        /**
         * The CSS class to use for the action button, defaults to `Modal-hashed__buttonPrimary__*`
         */
        actionBtnClass: PropTypes.string.isRequired,
        /**
         * Optional flag indicating whether the cancel button is rendered
         */
        showCancelBtn: PropTypes.bool,
        /**
         * Optional label for the cancel button when rendered
         */
        cancelBtnLabel: PropTypes.string,
        /**
         * Optional flag indicating whether the save button is rendered
         */
        showSaveBtn: PropTypes.bool,
        /**
         * Optional label for the save button when rendered
         */
        saveBtnLabel: PropTypes.string,
        /**
         * Optional callback to process the page on 'action'
         */
        onAction: PropTypes.func,
        /**
         * Optional callback to process the page on 'cancel'
         */
        onCancel: PropTypes.func,
        /**
         * Optional callback to process the page on 'save'
         */
        onSave: PropTypes.func,
        /**
         * Function from Modal which is called when "cancel" button is clicked
         */
        dismissAction: PropTypes.func,
        /**
         * Function from Modal which is called when "cancel" button is clicked
         */
        closeAction: PropTypes.func,
        /**
         * Specify whether title should be placed in the center
         */
        centerTitle: PropTypes.bool
        // renderActionBar: PropTypes.func,
        // renderTitleBar: PropTypes.func
    };

    static defaultProps = {
        actionBtnLabel: {
            id: 'platform.modal.ModalService.Ok',
            defaultMessage: 'Ok'
        },
        actionBtnClass: buttonStyles.primary,
        showCancelBtn: false,
        cancelBtnLabel: {
            id: 'platform.modal.ModalService.Close',
            defaultMessage: 'Close'
        },
        showSaveBtn: false,
        saveBtnLabel: {
            id: 'platform.modal.ModalService.Save',
            defaultMessage: 'Save'
        },
        centerTitle: false,
        theme: defaultTheme
    };

    /**
     * Define context properties that are set somewhere higher in the component chain.
     * Useful for passing down functions and settings without having to pass down as a prop.
     * Used here to obtain the translator function for localizing strings
     *
     * @type {Object}
     * @property {function(key: string)} translator - The function used to translate strings
     */
    static contextTypes = {
        translator: PropTypes.func.isRequired
    };

    /**
     * Callback function for the "action" button.  Stops the event propagation and then calls the `closeAction` callback
     *
     * @param {Object} evt - The event generated by the "action" button
     */
    handleAction = evt => {
        evt.stopPropagation();

        const { onAction, closeAction } = this.props;

        let result = true;
        let resultPromise;
        let resultException;

        if (_.isFunction(onAction)) {
            try {
                result = onAction();
                if (isPromise(result)) {
                    resultPromise = result;
                }
            } catch (ex) {
                result = false;
                if (ex !== false) {
                    resultException = ex;
                }
            }
        }

        // process non-promise result
        if (!resultPromise) {
            if (result !== false) {
                closeAction(result);
            } else if (resultException) {
                // FIX: need to figure out how we want to handle exceptions/knockouts inside of dialogs
                log.info(resultException);
            }
            return;
        }

        // process promise result
        resultPromise.then(promiseResult => {
            if (promiseResult !== false) {
                closeAction(promiseResult);
            }
        }).catch(ex => {
            if (ex !== false) {
                // FIX: need to figure out how we want to handle exceptions/knockouts inside of dialogs
                log.error(ex);
            }
        });
    };

    /**
     * Callback function for the "save" button.  Stops the event propagation, but does not close or dismiss the dialog
     *
     * @param {Object} evt - The event generated by the "cancel" button
     */
    handleSave = evt => {
        evt.stopPropagation();

        const {onSave} = this.props;
        if (_.isFunction(onSave)) {
            onSave();
        }
    };

    /**
     * Callback function for the "cancel" button.  Stops the event propagation and then calls the `dismissAction` callback
     *
     * @param {Object} evt - The event generated by the "cancel" button
     */
    handleCancel = evt => {
        evt.stopPropagation();
        const { onCancel, dismissAction } = this.props;

        let result = true;
        let resultPromise;
        let resultException;

        if (_.isFunction(onCancel)) {
            try {
                result = onCancel();
                if (isPromise(result)) {
                    resultPromise = result;
                }
            } catch (ex) {
                result = false;
                resultException = ex;
            }
        }


        // process non-promise result
        if (!resultPromise) {
            if (result !== false) {
                dismissAction();
            } else if (resultException) {
                // FIX: need to figure out how we want to handle exceptions/knockouts inside of dialogs
                log.info(resultException);
            }
            return;
        }

        // process promise result
        resultPromise.then(promiseResult => {
            if (promiseResult !== false) {
                dismissAction();
            }
        }).catch(ex => {
            // FIX: need to figure out how we want to handle exceptions/knockouts inside of dialogs
            log.error(ex);
        });

        // STORY: 'onCancel' is not triggered by clicking outside the dialog. Use full screen to prevent or we
        // should add the hook?
    };

    /**
     * Renders the header for the modal content's body.  The header changes colour depending on icon class type
     *
     * @param {String} themeStyles
     * @returns {ReactElement} - The rendered header
     */
    renderTitleBar(themeStyles) {
        // STORY: fix styling of title bar
        const {title, renderTitleBar, centerTitle} = this.props;

        const closeButton = (
            <button type="button" className={themeStyles.dismissButton} onClick={this.handleCancel}>
                <i className={getIconClasses('times')}></i>
            </button>
        );

        if (renderTitleBar) {
            return renderTitleBar(title);
        }

        const titleClasses = cx(themeStyles.modalTitle, {[themeStyles.centerTitle]: centerTitle});

        return (
            <h2 className={themeStyles.modalHeader}>
                <span className={titleClasses}>{title}</span>
                {closeButton}
            </h2>
        );

        // STORY: make 'renderTitleBar' more like wizard
        // renderTitleBar === false; doesn't show any action bar
        // renderTitleBar === function; invoke function to do the rendering
        // renderTitleBar === true or undefined; render default action bar

        // const {renderTitleBar} = this.props;
        // if (!renderTitleBar) {
        //     return null;
        // } else if (_.isFunction(renderTitleBar)) {
        //     return renderTitleBar(this.props.title, this.props.theme);
        // }
        // return (
        //     <DialogTitleBar title={this.props.title} theme={this.props.theme}/>
        // );
    }

    /**
     * Renders the footer portion of the modal content containing the "action" button and an optional "cancel" button.
     *
     * @param {String} themeStyles
     * @returns {ReactElement} - The rendered footer
     */
    renderActionBar(themeStyles) {

        const {
            showCancelBtn, cancelBtnLabel, showSaveBtn, saveBtnLabel, actionBtnLabel, actionBtnClass
        } = this.props;
        const translator = this.context.translator || defaultTranslator;
        const cancelButton = !showCancelBtn ? null : (
            <Button onClick={this.handleCancel} type='tertiary' className={themeStyles.modalButton}>
                {translator(cancelBtnLabel)}
            </Button>
        );
        const actionButtonClass = cx(
            themeStyles.modalButton,
            actionBtnClass
        );
        const saveButton = !showSaveBtn ? null : (
            <Button onClick={this.handleSave} type='secondary' className={themeStyles.modalButton}>
                {translator(saveBtnLabel)}
            </Button>
        );
        
        return (
            <div className={styles.modalFooter}>
                {cancelButton}
                {saveButton}
                <Button onClick={this.handleAction} className={actionButtonClass}>
                    {translator(actionBtnLabel)}
                </Button>
            </div>
        );

        // STORY: make 'renderActionBar' more like wizard
        // renderActionBar === false; doesn't show any action bar
        // renderActionBar === function; invoke function to do the rendering
        // renderActionBar === true or undefined; render default action bar

        // const {renderActionBar} = this.props;
        // if (!renderActionBar) {
        //     return null;
        // } else if (_.isFunction(renderActionBar)) {
        //     return renderActionBar(this.defineActions(), this.props.theme);
        // }
        // return (
        //     <WizardActionBar actions={this.defineActions()} theme={this.props.theme}/>
        // );
    }

    /**
     * Renders the ModalDialog component's header, body and footer
     *
     * @returns {ReactElement} - The rendered component
     */
    render() {
        const {className, children, theme} = this.props;

        const themeStyles = theme.getStyles('ModalDialog', styles);

        // STORY: fix up dialog layout so that `ModalService.showFullScreenPopup` takes up
        // the entire viewport and scrolls the content. Use 'cssgrid' or the new 'Grid' components
        // in elixir for this
        return (
            <div className={cx(className, themeStyles.bodyWrapper)}>
                {this.renderTitleBar(themeStyles)}
                <div className={themeStyles.modalBody}>
                    {children}
                </div>
                {this.renderActionBar(themeStyles)}
            </div>
        );
    }
}

export default withTheme(withModal(ModalDialog));
